import { msg } from "@lingui/core/macro";
import { createMemo, For, type JSX, Show } from "solid-js";
import { cn } from "@comandero/shared";
import { useI18n } from "@/providers/i18n-provider";

/**
 * ══════════════════════════════════════════════════════════════════════════
 * Inline theme-aware charts (NO heavy library)
 * ══════════════════════════════════════════════════════════════════════════
 *
 * Charts built with SVG/HTML + Tailwind classes of theme tokens
 * (accent/success/warning/info/destructive/muted). Inherit theme without hardcoded
 * (semantic: primary, accent, muted...). Accessible: each chart exposes
 * `role="img"` + `aria-label`, and series offer their textual value.
 */

/**
 * Categorical palette based on theme tokens (cycles). Only tokens defined
 * en global.css → theme-safe. Evita duplicar `primary` (= accent).
 */
const SERIES_BG = [
  "bg-accent",
  "bg-success",
  "bg-warning",
  "bg-brand-ember",
  "bg-destructive",
  "bg-brand-flame",
  "bg-accent/55",
  "bg-success/55",
] as const;

const SERIES_STROKE = [
  "stroke-accent",
  "stroke-success",
  "stroke-warning",
  "stroke-brand-ember",
  "stroke-destructive",
  "stroke-brand-flame",
] as const;

function seriesColor(index: number): string {
  return SERIES_BG[index % SERIES_BG.length] ?? "bg-accent";
}

interface ChartDatum {
  label: string;
  value: number;
  /** Pre-formatted display text (e.g. money). If missing, uses value. */
  display?: string;
  /** Forced color index; if missing uses position. */
  colorIndex?: number;
}

/* ───────────────────────────── Bar chart ───────────────────────────── */

interface BarChartProps {
  data: ChartDatum[];
  /** Accessible label for the entire chart.*/
  ariaLabel: string;
  /** Uses single color (accent) instead of categorical palette. */
  monochrome?: boolean;
  class?: string;
}

/**
 * Barras horizontales (HTML, no SVG): robustas a textos largos, legibles y
 * easy to make accessible. Each row shows label + bar + value.
 */
export function BarChart(props: BarChartProps) {
  const max = createMemo(() => Math.max(1, ...props.data.map((d) => Math.max(0, d.value))));
  return (
    <div class={cn("flex flex-col gap-2.5", props.class)} role="img" aria-label={props.ariaLabel}>
      <For each={props.data}>
        {(d, i) => {
          const pct = () => Math.round((Math.max(0, d.value) / max()) * 100);
          const color = () => (props.monochrome ? "bg-accent" : seriesColor(d.colorIndex ?? i()));
          return (
            <div class="flex flex-col gap-1">
              <div class="flex items-baseline justify-between gap-2 text-sm">
                <span class="min-w-0 truncate text-foreground">{d.label}</span>
                <span class="shrink-0 font-medium tabular-nums text-muted-foreground">
                  {d.display ?? String(d.value)}
                </span>
              </div>
              <div class="h-2.5 w-full overflow-hidden rounded-full bg-muted/40">
                {/* GPU-only: scaleX from the left edge instead of animating width. */}
                <div
                  class={cn(
                    "h-full w-full origin-left rounded-full transition-transform duration-500",
                    color(),
                  )}
                  style={{ transform: `scaleX(${pct() / 100})` }}
                />
              </div>
            </div>
          );
        }}
      </For>
    </div>
  );
}

/* ───────────────────────────── Column chart (vertical bars) ───────────── */

interface ColumnChartProps {
  data: ChartDatum[];
  ariaLabel: string;
  class?: string;
}

/**
 * Vertical bars (SVG) for short time series (hours/days). Scroll
 * NEVER horizontal: the container divides available width between columns.
 */
export function ColumnChart(props: ColumnChartProps) {
  const max = createMemo(() => Math.max(1, ...props.data.map((d) => Math.max(0, d.value))));
  return (
    <div
      class={cn("flex items-end gap-1.5", props.class)}
      role="img"
      aria-label={props.ariaLabel}
      style={{ "min-height": "8rem" }}
    >
      <For each={props.data}>
        {(d) => {
          const pct = () => Math.max(2, Math.round((Math.max(0, d.value) / max()) * 100));
          return (
            <div class="flex min-w-0 flex-1 flex-col items-center gap-1">
              <div class="flex h-28 w-full items-end justify-center">
                {/* GPU-only: scaleY from the bottom edge instead of animating height. */}
                <div
                  class="h-full w-full max-w-8 origin-bottom rounded-t-md bg-accent transition-transform duration-500"
                  style={{ transform: `scaleY(${pct() / 100})` }}
                  title={`${d.label}: ${d.display ?? String(d.value)}`}
                />
              </div>
              <span class="w-full truncate text-center text-[10px] tabular-nums text-muted-foreground">
                {d.label}
              </span>
            </div>
          );
        }}
      </For>
    </div>
  );
}

/* ───────────────────────────── Line chart ──────────────────────────── */

interface LineChartProps {
  data: ChartDatum[];
  ariaLabel: string;
  class?: string;
}

/** Coordenadas del viewBox 0..100 (SVG).*/
const VIEW = 100;
const VIEW_MID = 50;
/** Vertical margin: leaves 4 top/bottom so line does not touch border. */
const VERT_PAD = 4;
const VERT_RANGE = VIEW - VERT_PAD * 2;

/** Line (SVG, viewBox 0..100) with subtle area. Theme-aware via fill/stroke classes. */
export function LineChart(props: LineChartProps) {
  const points = createMemo(() => {
    // oxlint-disable-next-line prefer-destructuring -- destructuring props breaks Solid reactivity
    const data = props.data;
    if (data.length === 0) {
      return { line: "", area: "" };
    }
    const max = Math.max(1, ...data.map((d) => Math.max(0, d.value)));
    const stepX = data.length > 1 ? VIEW / (data.length - 1) : 0;
    const coords = data.map((d, i) => {
      const x = data.length > 1 ? i * stepX : VIEW_MID;
      const y = VIEW - (Math.max(0, d.value) / max) * VERT_RANGE - VERT_PAD;
      return `${x.toFixed(2)},${y.toFixed(2)}`;
    });
    const line = coords.join(" ");
    const area = `0,${VIEW} ${line} ${VIEW},${VIEW}`;
    return { line, area };
  });

  return (
    <div class={cn("w-full", props.class)} role="img" aria-label={props.ariaLabel}>
      <svg viewBox="0 0 100 100" preserveAspectRatio="none" class="h-32 w-full" aria-hidden="true">
        <polygon points={points().area} class="fill-accent/15" />
        <polyline
          points={points().line}
          fill="none"
          class="stroke-accent"
          stroke-width="1.5"
          stroke-linecap="round"
          stroke-linejoin="round"
          vector-effect="non-scaling-stroke"
        />
      </svg>
    </div>
  );
}

/* ───────────────────────────── Donut chart ─────────────────────────── */

interface DonutChartProps {
  data: ChartDatum[];
  ariaLabel: string;
  /** Texto central (p.ej. total). */
  centerLabel?: JSX.Element;
  class?: string;
}

const RADIUS = 40;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;

/** Donut (SVG dasharray) + leyenda. Theme-aware. */
export function DonutChart(props: DonutChartProps) {
  const { t } = useI18n();
  const total = createMemo(() => props.data.reduce((acc, d) => acc + Math.max(0, d.value), 0));

  const segments = createMemo(() => {
    const sum = total();
    if (sum <= 0) {
      return [];
    }
    let offset = 0;
    return props.data.map((d, i) => {
      const frac = Math.max(0, d.value) / sum;
      const len = frac * CIRCUMFERENCE;
      const seg = {
        datum: d,
        colorIndex: d.colorIndex ?? i,
        dash: `${len} ${CIRCUMFERENCE - len}`,
        offset: -offset,
        pct: Math.round(frac * 100),
      };
      offset += len;
      return seg;
    });
  });

  return (
    <div class={cn("flex flex-col items-center gap-4 sm:flex-row sm:gap-6", props.class)}>
      <div class="relative shrink-0">
        <svg
          viewBox="0 0 100 100"
          class="h-32 w-32 -rotate-90"
          role="img"
          aria-label={props.ariaLabel}
        >
          <circle
            cx="50"
            cy="50"
            r={RADIUS}
            fill="none"
            class="stroke-muted/40"
            stroke-width="14"
          />
          <For each={segments()}>
            {(s) => (
              <circle
                cx="50"
                cy="50"
                r={RADIUS}
                fill="none"
                class={SERIES_STROKE[s.colorIndex % SERIES_STROKE.length] ?? "stroke-accent"}
                stroke-width="14"
                stroke-dasharray={s.dash}
                stroke-dashoffset={s.offset}
              />
            )}
          </For>
        </svg>
        <Show when={props.centerLabel}>
          <div class="absolute inset-0 flex flex-col items-center justify-center text-center">
            {props.centerLabel}
          </div>
        </Show>
      </div>
      <ul class="flex w-full flex-col gap-1.5">
        <Show
          when={segments().length > 0}
          fallback={<li class="text-sm text-muted-foreground">{t(msg`No data yet`)}</li>}
        >
          <For each={segments()}>
            {(s) => (
              <li class="flex items-center gap-2 text-sm">
                <span
                  class={cn("h-3 w-3 shrink-0 rounded-sm", seriesColor(s.colorIndex))}
                  aria-hidden="true"
                />
                <span class="min-w-0 flex-1 truncate text-foreground">{s.datum.label}</span>
                <span class="shrink-0 tabular-nums text-muted-foreground">
                  {s.datum.display ?? String(s.datum.value)}
                  <span class="ml-1 text-xs text-muted-foreground/70">({s.pct}%)</span>
                </span>
              </li>
            )}
          </For>
        </Show>
      </ul>
    </div>
  );
}
